import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { SubscriptionBanner } from './SubscriptionBanner';
import { CopilotPanel } from './CopilotPanel';
import { businessProfileService } from '@/services/business-profile.service';
import { useAuth } from '@/hooks/useAuth';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { PageGuard } from './PageGuard';
import { ModuleAccessProvider, useModuleAccess } from '@/contexts/ModuleAccessContext';
import { ModulePageGuard } from './ModulePageGuard';
import { ProviderConnectGate } from './ProviderConnectGate';

const PAGE_VARIANTS = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -8 },
};

const PAGE_TRANSITION = {
  duration: 0.16,
  ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
};

export const Layout: React.FC = () => <ModuleAccessProvider><WorkspaceLayout /></ModuleAccessProvider>;

const WorkspaceLayout: React.FC = () => {
  const location = useLocation();
  const shouldReduce = useReducedMotion();
  const { user } = useAuth();
  const { has } = useModuleAccess();
  const copilotEnabled = has('ai_copilot');

  // ── First-login redirect ─────────────────────────────────────────────────
  // company_admins land on /onboarding when they haven't created a business
  // profile yet. company_users skip the check (admins only do onboarding).
  const [profileChecked, setProfileChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // ── Global copilot panel ─────────────────────────────────────────────────
  const [copilotOpen, setCopilotOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (copilotEnabled && (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setCopilotOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [copilotEnabled]);

  useEffect(() => {
    if (user?.role !== 'company_admin') { setProfileChecked(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const profile = await businessProfileService.getByCompany();
        if (!cancelled) setNeedsOnboarding(!profile);
      } catch { /* ignore — guard or other failure */ }
      finally { if (!cancelled) setProfileChecked(true); }
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  if (profileChecked && needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <WorkspaceProvider>
    <div className="flex h-screen overflow-hidden bg-bg">
      <Sidebar />
      <main className="relative min-w-0 flex-1 overflow-y-auto">
        <SubscriptionBanner />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            variants={shouldReduce ? undefined : PAGE_VARIANTS}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={PAGE_TRANSITION}
            className="h-full"
          >
            <ModulePageGuard><PageGuard><ProviderConnectGate>
              <Outlet />
            </ProviderConnectGate></PageGuard></ModulePageGuard>
          </motion.div>
        </AnimatePresence>

        {/* Copilot launcher — a tab docked to the right EDGE, vertically
            centred. It used to be a pill floating at bottom-right, where it
            covered page actions and form fields; the edge keeps it reachable
            without ever sitting on top of content. Widens on hover to name
            itself. Suppressed on /chats, which has its own inline composer.
            ⌘K opens it anywhere regardless. */}
        {copilotEnabled && !location.pathname.startsWith('/chats') && (
        <button
          onClick={() => setCopilotOpen(true)}
          className="group fixed right-0 top-1/2 z-30 flex -translate-y-1/2 items-center gap-2 rounded-l-2xl border border-r-0 border-primary/30 bg-primary py-3 pl-3 pr-2 text-text-on-primary shadow-elevated transition-[padding] duration-200 hover:pl-4 hover:pr-3"
          aria-label="Open copilot (⌘K)"
          title="Ask copilot (⌘K)"
        >
          <Sparkles size={17} className="shrink-0" />
          {/* Collapsed to nothing until hover — the tab stays a thin sliver. */}
          <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-semibold opacity-0 transition-all duration-200 group-hover:max-w-[120px] group-hover:opacity-100">
            Ask copilot
          </span>
        </button>
        )}
      </main>

      {copilotEnabled && <CopilotPanel open={copilotOpen} onClose={() => setCopilotOpen(false)} />}
    </div>
    </WorkspaceProvider>
  );
};
